<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white">
          <v-toolbar-title class="title">{{ $t("Product Group Management") }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2">
        <v-card-text>
          <v-row align="center">
            <!-- Search Section -->
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-2 search-panel">
                <v-row no-gutters style="display: flex;align-items: center;gap: 18px;padding: 10px;">
                  <v-text-field
                    label="Group Name"
                    density="comfortable"
                    variant="outlined"
                    prepend-inner-icon="mdi-text-box-search-outline"
                    hide-details
                    clearable
                    v-model="searchName"
                    @keydown.enter="Search"
                    color="primary"
                  ></v-text-field>
                  <v-btn
                    color="primary"
                    class="ml-2"
                    variant="elevated"
                    style="min-width: 100px;"
                    @click="Search"
                  >
                    <v-icon start>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                </v-row>
              </v-card>
            </v-col>

            <!-- Create Button -->
            <v-col cols="12" md="6" class="d-flex flex-wrap justify-end gap-2">
              <v-btn
                color="primary"
                class="control-btn create-btn"
                variant="flat"
                size="large"
                prepend-icon="mdi-plus-circle"
                @click="openCreateDialog"
                rounded="lg"
                v-if="permissions.add"
              >
                CREATE GROUP
                <v-tooltip activator="parent" location="top">
                  Create new Product Group
                </v-tooltip>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Data Table -->
        <v-data-table-server
          v-model:options="options"
          :items-per-page="itemsPerPage"
          :items-length="totalItems"
          :items="groups"
          :headers="headers"
          :loading="loading"
        >
          <!-- No Data Template -->
          <template v-slot:no-data>
            <div class="no-data-container">
              <div class="no-data-content">
                <v-icon size="64" color="grey-lighten-2" class="no-data-icon">
                  mdi-package-variant
                </v-icon>
                <div class="no-data-text">
                  <h3 class="no-data-title">No Product Groups Found</h3>
                  <p class="no-data-subtitle">Create your first product group to organize your products.</p>
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item="{ item }">
            <tr>
              <td class="text-center">{{ item.number }}</td>
              <td>
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-folder-multiple</v-icon>
                  {{ item.groupName }}
                </div>
              </td>
              <td>{{ item.description || '-' }}</td>
              <td class="text-center">
                <v-chip color="primary" variant="tonal" size="small">
                  {{ item.groupCount }} groups
                </v-chip>
              </td>
              <td class="text-center">{{ item.createBy }}</td>
              <td class="text-center">{{ formatDateTime(item.createDate) }}</td>
              <td class="text-center">
                <div class="action-group">
                  <v-btn
                    variant="text"
                    density="comfortable"
                    @click="viewGroup(item)"
                    class="action-btn view-btn"
                    icon
                  >
                    <v-icon size="18">mdi-eye-outline</v-icon>
                    <v-tooltip activator="parent" location="top">View Product Groups</v-tooltip>
                  </v-btn>

                  <v-btn
                    variant="text"
                    density="comfortable"
                    @click="openEditDialog(item)"
                    class="action-btn edit-btn"
                    icon
                    v-if="permissions.edit || permissions.view"
                  >
                    <v-icon size="18">mdi-pencil-outline</v-icon>
                    <v-tooltip activator="parent" location="top">Edit Group</v-tooltip>
                  </v-btn>

                  <v-btn
                    variant="text"
                    density="comfortable"
                    @click="openDeleteDialog(item)"
                    class="action-btn delete-btn"
                    icon
                    v-if="permissions.delete"
                  >
                    <v-icon size="18">mdi-delete-outline</v-icon>
                    <v-tooltip activator="parent" location="top">Delete Group</v-tooltip>
                  </v-btn>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </v-card>
    </v-container>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="formDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">{{ isEdit ? (permissions.edit ? 'mdi-pencil' : 'mdi-eye-outline') : 'mdi-plus-circle' }}</v-icon>
          {{ isEdit ? (permissions.edit ? 'Edit Product Group' : 'View Product Group') : 'Create Product Group' }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.groupName"
              label="Group Name *"
              variant="outlined"
              density="comfortable"
              :rules="[v => !!v || 'Group name is required']"
              prepend-inner-icon="mdi-folder"
            ></v-text-field>

            <v-textarea
              v-model="formData.description"
              label="Description"
              variant="outlined"
              density="comfortable"
              rows="2"
              prepend-inner-icon="mdi-text"
            ></v-textarea>

            <v-divider class="my-4"></v-divider>

            <div class="text-subtitle-1 font-weight-medium mb-2">
              <v-icon class="mr-1">mdi-package-variant</v-icon>
              Select Product Groups
            </div>

            <v-autocomplete
              v-model="formData.productGroupIds"
              :items="ddProductGroup"
              item-title="productGroupName"
              item-value="productGroupID"
              label="Product Groups"
              variant="outlined"
              density="comfortable"
              multiple
              chips
              closable-chips
              :loading="loadingProductGroups"
              prepend-inner-icon="mdi-database-search"
            >
              <template v-slot:chip="{ props, item }">
                <v-chip
                  v-bind="props"
                  color="primary"
                  variant="tonal"
                  closable
                >
                  <v-icon start size="small">mdi-package-variant</v-icon>
                  {{ item.title }}
                </v-chip>
              </template>
            </v-autocomplete>

            <div v-if="formData.productGroupIds.length > 0" class="mt-2">
              <v-chip color="info" variant="tonal" size="small">
                {{ formData.productGroupIds.length }} product group(s) selected
              </v-chip>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="closeFormDialog"
          >
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="saveGroup"
            :loading="saving"
            :disabled="!formValid"
            v-if="isEdit ? permissions.edit : permissions.add"
          >
            <v-icon start>mdi-content-save</v-icon>
            {{ isEdit ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Product Groups Dialog -->
    <v-dialog v-model="viewDialog" max-width="600px">
      <v-card>
        <v-card-title class="bg-primary text-white">
          <v-icon class="mr-2">mdi-folder-open</v-icon>
          {{ selectedGroup?.groupName }}
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="selectedGroup?.description" class="mb-4 text-grey">
            {{ selectedGroup.description }}
          </div>

          <div class="text-subtitle-1 font-weight-medium mb-2">
            Product Groups in this collection:
          </div>

          <v-list v-if="viewProductGroups.length > 0" density="compact">
            <v-list-item
              v-for="(pg, index) in viewProductGroups"
              :key="index"
              :title="pg.productGroupName"
            >
              <template v-slot:prepend>
                <v-icon color="primary">mdi-package-variant</v-icon>
              </template>
              <template v-slot:append>
                <v-chip size="x-small" variant="tonal">{{ index + 1 }}</v-chip>
              </template>
            </v-list-item>
          </v-list>

          <v-alert v-else type="info" variant="tonal">
            No product groups in this collection.
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="viewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            This action cannot be undone.
          </v-alert>

          <p>Are you sure you want to delete the product group:</p>
          <p class="font-weight-bold text-primary">{{ selectedGroup?.groupName }}</p>
          <p class="text-grey">{{ selectedGroup?.groupCount }} product group(s) will be removed from this collection.</p>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="deleteDialog = false">
            <v-icon start>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            @click="confirmDelete"
            :loading="deleting"
            v-if="permissions.delete"
          >
            <v-icon start>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Cookies from "js-cookie";

export default {
  name: "ProductGroupManagement",
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),
      searchName: '',
      groups: [],
      loading: false,

      // Table options
      options: {},
      itemsPerPage: 10,
      totalItems: 0,
      headers: [
        { title: 'No.', key: 'number', align: 'center', sortable: false, width: '80px' },
        { title: 'Group Name', key: 'groupName', align: 'start', sortable: true },
        { title: 'Description', key: 'description', align: 'start', sortable: false },
        { title: 'Product Groups', key: 'groupCount', align: 'center', sortable: true, width: '140px' },
        { title: 'Created By', key: 'createBy', align: 'center', sortable: true, width: '150px' },
        { title: 'Created Date', key: 'createDate', align: 'center', sortable: true, width: '150px' },
        { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '150px' }
      ],

      // Form dialog
      formDialog: false,
      formValid: false,
      isEdit: false,
      formData: {
        id: null,
        groupName: '',
        description: '',
        productGroupIds: []
      },
      saving: false,

      // View dialog
      viewDialog: false,
      viewProductGroups: [],
      selectedGroup: null,

      // Delete dialog
      deleteDialog: false,
      deleting: false,

      // Product Groups dropdown
      ddProductGroup: [],
      loadingProductGroups: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // User info
      roleId: Cookies.get("roleId"),
    }
  },
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('ProductGroup');
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('ProductGroup'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('ProductGroup'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('ProductGroup'),
      }
    },
  },
  watch: {
    options: {
      handler() {
        this.loadGroups()
      },
      deep: true
    }
  },
  mounted() {
    this.loadGroups()
    this.loadProductGroups()
  },
  methods: {
    formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('th-TH', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    async loadGroups() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy } = this.options
        const requestData = {
          page: page || 1,
          perPage: itemsPerPage || 10,
          name: this.searchName || null,
          sortBy: sortBy?.[0]?.key || null,
          sortDesc: sortBy?.[0]?.order === 'desc' || false
        }

        this.$call.GetTableProductGroupCollection(requestData,
          (response) => {
            if (response.status === 0) {
              this.groups = response.data.data || []
              this.totalItems = response.data.total || 0
            } else {
              this.showError('Failed to load product groups')
            }
            this.loading = false
          },
          (error) => {
            console.error('Error loading groups:', error)
            this.showError('Failed to load product groups')
            this.loading = false
          }
        )
      } catch (error) {
        console.error('Error:', error)
        this.loading = false
      }
    },

    async loadProductGroups() {
      this.loadingProductGroups = true
      this.$call.GetDDProductGroupList(
        (response) => {
          if (response.status === 0) {
            this.ddProductGroup = response.data || []
          }
          this.loadingProductGroups = false
        },
        (error) => {
          console.error('Error loading product groups:', error)
          this.loadingProductGroups = false
        }
      )
    },

    Search() {
      this.options = { ...this.options, page: 1 }
      this.loadGroups()
    },

    openCreateDialog() {
      this.isEdit = false
      this.formData = {
        id: null,
        groupName: '',
        description: '',
        productGroupIds: []
      }
      this.formDialog = true
    },

    openEditDialog(group) {
      this.isEdit = true
      this.selectedGroup = group

      // Load group details
      this.$call.GetProductGroupCollectionById(group.id,
        (response) => {
          if (response.status === 0) {
            const data = response.data
            this.formData = {
              id: data.id,
              groupName: data.groupName,
              description: data.description || '',
              productGroupIds: data.productGroups?.map(pg => pg.productGroupId) || []
            }
            this.formDialog = true
          } else {
            this.showError('Failed to load group details')
          }
        },
        (error) => {
          console.error('Error:', error)
          this.showError('Failed to load group details')
        }
      )
    },

    closeFormDialog() {
      this.formDialog = false
      this.formData = {
        id: null,
        groupName: '',
        description: '',
        productGroupIds: []
      }
    },

    async saveGroup() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      const request = {
        groupName: this.formData.groupName,
        description: this.formData.description,
        productGroupIds: this.formData.productGroupIds
      }

      if (this.isEdit) {
        request.id = this.formData.id
        this.$call.UpdateProductGroupCollection(request,
          (response) => {
            if (response.status === 0) {
              this.showSuccess('Product group updated successfully')
              this.closeFormDialog()
              this.loadGroups()
            } else {
              this.showError(response.message || 'Failed to update group')
            }
            this.saving = false
          },
          (error) => {
            console.error('Error:', error)
            this.showError('Failed to update group')
            this.saving = false
          }
        )
      } else {
        this.$call.CreateProductGroupCollection(request,
          (response) => {
            if (response.status === 0) {
              this.showSuccess('Product group created successfully')
              this.closeFormDialog()
              this.loadGroups()
            } else {
              this.showError(response.message || 'Failed to create group')
            }
            this.saving = false
          },
          (error) => {
            console.error('Error:', error)
            this.showError('Failed to create group')
            this.saving = false
          }
        )
      }
    },

    viewGroup(group) {
      this.selectedGroup = group
      this.$call.GetProductGroupCollectionById(group.id,
        (response) => {
          if (response.status === 0) {
            this.viewProductGroups = response.data.productGroups || []
            this.viewDialog = true
          } else {
            this.showError('Failed to load group details')
          }
        },
        (error) => {
          console.error('Error:', error)
          this.showError('Failed to load group details')
        }
      )
    },

    openDeleteDialog(group) {
      this.selectedGroup = group
      this.deleteDialog = true
    },

    async confirmDelete() {
      this.deleting = true
      this.$call.DeleteProductGroupCollection(this.selectedGroup.id,
        (response) => {
          if (response.status === 0) {
            this.showSuccess('Product group deleted successfully')
            this.deleteDialog = false
            this.loadGroups()
          } else {
            this.showError(response.message || 'Failed to delete group')
          }
          this.deleting = false
        },
        (error) => {
          console.error('Error:', error)
          this.showError('Failed to delete group')
          this.deleting = false
        }
      )
    },

    showSuccess(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'success'
      this.snackbar = true
    },

    showError(message) {
      this.snackbarMessage = message
      this.snackbarColor = 'error'
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.header-title {
  border-radius: 8px 8px 0 0;
}

.card2 {
  border-radius: 0 0 8px 8px;
}

.search-panel {
  border-color: #e0e0e0;
}

.no-data-container {
  padding: 60px 20px;
  text-align: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.no-data-title {
  color: #666;
  font-weight: 500;
  margin-bottom: 8px;
}

.no-data-subtitle {
  color: #999;
  font-size: 14px;
}

.action-group {
  display: flex;
  justify-content: center;
  gap: 4px;
}

.action-btn {
  min-width: 36px;
  height: 36px;
}

.action-btn.view-btn:hover {
  color: #1976D2;
  background-color: rgba(25, 118, 210, 0.1);
}

.action-btn.edit-btn:hover {
  color: #FF9800;
  background-color: rgba(255, 152, 0, 0.1);
}

.action-btn.delete-btn:hover {
  color: #F44336;
  background-color: rgba(244, 67, 54, 0.1);
}
</style>
